import { useMediaQuery } from '@mui/material';
import { OverlayTrigger, Tooltip } from '@rio-cloud/rio-uikit';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { formatOccurrenceStepInfoText } from '../../../enums/occurrenceStep';
import type { Occurrence, OccurrenceWithTimeSpent } from '../../../models/occurrence';
import { GenericCard } from '../../kanban/components/genericCards';
import { useStickyTop, useSyncedScroll } from '../hooks/useKanbanScroll';
import { findStep, getStepLabelId, isNextStampOccurrence, toStepValue } from '../trackingUtils';
import type { StageSummary, StepCardProps, SummaryProps } from '../types';
import KanbanStepCard from './KanbanStepCard';
import KanbanVehicleCard from './KanbanVehicleCard';
import StageHeat from './StageHeat';
import useMtText from './useMtText';
import './TrackingKanban.scss';

type SortDirection = 'ASC' | 'DESC';

interface ITrackingKanbanProps {
    rows: Occurrence[];
    stepKeys: string[];
    stages: StageSummary[];
    timeSpentById: Map<string, OccurrenceWithTimeSpent>;
    loading: boolean;
    activeStage: string | null;
    onStageClick: (step: string) => void;
    sortKey: string;
    sortDirection: SortDirection;
    onSort: (key: string, direction: SortDirection) => void;
    stepCardProps: StepCardProps;
    summaryProps: SummaryProps;
}

const STEP_WIDTH = 240;
const VEHICLE_WIDTH = 300;
const GAP = 10;
const PADDING = 10;
const SKELETON_ROWS = ['skeleton-1', 'skeleton-2', 'skeleton-3', 'skeleton-4'];

const VehicleSkeleton = () => (
    <div className='mt-kvc mt-kb__skel'>
        <span className='mt-skel' />
        <span className='mt-skel' />
        <span className='mt-skel' />
    </div>
);

const TrackingKanban = ({
    rows,
    stepKeys,
    stages,
    timeSpentById,
    loading,
    activeStage,
    onStageClick,
    sortKey,
    sortDirection,
    onSort,
    stepCardProps,
    summaryProps,
}: ITrackingKanbanProps) => {
    const intl = useIntl();
    const { t, msg, formatNumber, formatDuration } = useMtText();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const stickyRef = useRef<HTMLDivElement>(null);
    const headRef = useRef<HTMLDivElement>(null);
    const barRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);

    useStickyTop(stickyRef);
    useSyncedScroll(bodyRef, headRef, barRef);

    const columns = isMobile
        ? [`${STEP_WIDTH}px`, ...stepKeys.map(() => `${STEP_WIDTH}px`)]
        : stepKeys.map(() => `${STEP_WIDTH}px`);
    const scrollWidth = PADDING * 2 + columns.length * STEP_WIDTH + (columns.length - 1) * GAP;
    const gridStyle = { width: scrollWidth, gridTemplateColumns: columns.join(' ') };
    const fixedStyle = { width: VEHICLE_WIDTH + PADDING, gridTemplateColumns: `${VEHICLE_WIDTH}px` };
    const stageByStep = new Map(stages.map(stage => [stage.step, stage]));
    const showSkeleton = loading && !rows.length;
    const vehicleLabel = msg('intl-msg:kanban.cardView.vehicle');

    const vehicleHeader = (
        <div className='mt-kb__h mt-kb__h--vehicle'>
            <span className='mt-kb__name'>{vehicleLabel}</span>
        </div>
    );

    const renderVehicle = (occurrence: Occurrence) => (
        <KanbanVehicleCard
            occurrence={occurrence}
            timeSpent={timeSpentById.get(occurrence.id)}
            summaryProps={summaryProps}
        />
    );

    return (
        <div className={`mt-kb${isMobile ? ' mt-kb--mobile' : ''}`}>
            <div className='mt-kb__head' ref={stickyRef}>
                <div className='mt-kb__split'>
                    {!isMobile && (
                        <div className='mt-kb__grid mt-kb__grid--fixed mt-kb__fixedhead' style={fixedStyle}>
                            {vehicleHeader}
                        </div>
                    )}
                    <div className='mt-kb__scrollside'>
                        <div className='mt-kb__bar' ref={barRef}>
                            <div style={{ width: scrollWidth }} />
                        </div>
                        <div className='mt-kb__headscroll' ref={headRef}>
                            <div className='mt-kb__grid' style={gridStyle}>
                                {isMobile && vehicleHeader}
                                {stepKeys.map(stepKey => {
                                    const step = toStepValue(stepKey);
                                    const name = msg(getStepLabelId(step));
                                    const infoText = formatOccurrenceStepInfoText(intl, step);
                                    const stage = stageByStep.get(step);
                                    const shownDirection = sortKey === step ? sortDirection : 'DESC';
                                    return (
                                        <div key={stepKey} className='mt-kb__h'>
                                            <div className='mt-kb__hrow'>
                                                <button
                                                    type='button'
                                                    className='mt-kb__sort'
                                                    onClick={() =>
                                                        onSort(step, shownDirection === 'ASC' ? 'DESC' : 'ASC')
                                                    }
                                                >
                                                    <span
                                                        className='mt-kb__arrows'
                                                        data-active={sortKey === step}
                                                        data-dir={shownDirection}
                                                    >
                                                        <span className='rioglyph rioglyph-triangle-top' />
                                                        <span className='rioglyph rioglyph-triangle-bottom' />
                                                    </span>
                                                    <span className='mt-kb__name'>{name}</span>
                                                </button>
                                                {infoText && (
                                                    <OverlayTrigger
                                                        trigger='click'
                                                        placement='bottom'
                                                        rootClose
                                                        overlay={
                                                            <Tooltip id={`mt-kb-info-${stepKey}`} allowOnTouch>
                                                                <div className='mt-kb__tip'>
                                                                    <b>{name}</b>
                                                                    <span>{infoText}</span>
                                                                </div>
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <button type='button' className='mt-kb__info' aria-label={name}>
                                                            <span className='rioglyph rioglyph-info-sign' />
                                                        </button>
                                                    </OverlayTrigger>
                                                )}
                                            </div>
                                            {stage && (
                                                <button
                                                    type='button'
                                                    className='mt-kb__stat'
                                                    aria-pressed={activeStage === step}
                                                    title={
                                                        stage.estimateMs
                                                            ? t('summary.estimated', {
                                                                  time: formatDuration(stage.estimateMs),
                                                              })
                                                            : undefined
                                                    }
                                                    onClick={() => onStageClick(step)}
                                                >
                                                    <span className='mt-stage__time mt-num'>
                                                        {formatDuration(stage.averageMs)}
                                                    </span>
                                                    <span className='mt-kb__count mt-num'>
                                                        {t('summary.vehicles', { count: formatNumber(stage.count) })}
                                                    </span>
                                                    <StageHeat stage={stage} />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='mt-kb__split'>
                {!isMobile && (
                    <div className='mt-kb__grid mt-kb__grid--fixed mt-kb__grid--rows' style={fixedStyle}>
                        {showSkeleton && SKELETON_ROWS.map(rowKey => <VehicleSkeleton key={rowKey} />)}
                        {rows.map(occurrence => (
                            <div key={occurrence.id} className='mt-kb__veh'>
                                {renderVehicle(occurrence)}
                            </div>
                        ))}
                    </div>
                )}

                <div className='mt-kb__body' ref={bodyRef}>
                    <div className='mt-kb__grid mt-kb__grid--rows' style={gridStyle}>
                        {showSkeleton &&
                            SKELETON_ROWS.map(rowKey => (
                                <div key={rowKey} className='mt-kb__row'>
                                    {isMobile && <VehicleSkeleton />}
                                    {stepKeys.map(stepKey => (
                                        <div key={stepKey} className='mt-ksc mt-ksc--todo mt-kb__skel'>
                                            <span className='mt-skel' />
                                            <span className='mt-skel' />
                                        </div>
                                    ))}
                                </div>
                            ))}

                        {rows.map(occurrence => (
                            <div key={occurrence.id} className='mt-kb__row'>
                                {isMobile && <div className='mt-kb__veh'>{renderVehicle(occurrence)}</div>}
                                {stepKeys.map(stepKey => {
                                    const step = toStepValue(stepKey);
                                    return (
                                        <GenericCard
                                            key={`${occurrence.id}-${stepKey}`}
                                            {...stepCardProps}
                                            occurrence={occurrence}
                                            summaryStep={findStep(occurrence, step)}
                                            step={step}
                                            finalized={!!occurrence.finished}
                                            isNextStamp={isNextStampOccurrence(occurrence)}
                                            renderCard={card => <KanbanStepCard card={card} />}
                                        />
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TrackingKanban;
